import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Plus, Edit, Trash2, ArrowLeft, Eye, 
  Home, Info, HelpCircle, MoreVertical, Globe, Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

export const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const response = await adminFetch('/admin/pages');
      const data = await response.json();
      setPages(data);
    } catch (error) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    try {
      const response = await adminFetch(`/admin/pages/${pageId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Page deleted');
        setPages(pages.filter(p => p.id !== pageId));
        setDeleteConfirm(null);
      }
    } catch (error) {
      toast.error('Failed to delete page');
    }
  };

  const getPageIcon = (slug) => {
    switch (slug) {
      case 'home': return <Home className="w-5 h-5" />;
      case 'about': return <Info className="w-5 h-5" />;
      case 'help': return <HelpCircle className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const systemPages = ['home', 'about', 'help'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold font-display text-foreground">
                Pages
              </h1>
              <p className="text-muted-foreground mt-1">
                Create and edit your website pages
              </p>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/admin/pages/new')}
            className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            New Page
          </button>
        </div>

        {/* Pages List */}
        <div className="space-y-4">
          {pages.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
              <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-foreground mb-2">No pages yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first page to get started
              </p>
              <button
                onClick={() => navigate('/admin/pages/new')}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all"
              >
                Create Page
              </button>
            </div>
          ) : (
            pages.map((page, index) => (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card border border-border/50 rounded-2xl p-6 hover:border-primary/30 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      {getPageIcon(page.slug)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-foreground">
                          {page.title}
                        </h3>
                        {systemPages.includes(page.slug) && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                            System
                          </span>
                        )}
                        {page.published ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                            <Globe className="w-3 h-3" /> Published
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-stone-500/20 text-stone-400 text-xs font-medium rounded-full">
                            <Lock className="w-3 h-3" /> Draft
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        /{page.slug}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <a
                      href={`/${page.slug === 'home' ? '' : page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-muted-foreground hover:text-foreground hover:bg-background rounded-lg transition-colors"
                      title="View Page"
                    >
                      <Eye className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => navigate(`/admin/pages/edit/${page.id}`)}
                      className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit Page"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    {!systemPages.includes(page.slug) && (
                      <button
                        onClick={() => setDeleteConfirm(page)}
                        className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete Page"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Block count */}
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="text-sm text-muted-foreground">
                    {page.blocks?.length || 0} content blocks
                  </p>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
              onClick={() => setDeleteConfirm(null)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-card border border-border rounded-2xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-foreground mb-2">Delete Page?</h3>
                <p className="text-muted-foreground mb-6">
                  Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 px-4 py-3 bg-background border border-border rounded-xl font-medium text-foreground hover:bg-card transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirm.id)}
                    className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-medium text-white transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
